import { isSameOriginPath } from "@/lib/app-links";
import { cn } from "@/lib/utils";
import { ArrowRightIcon, ListIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

type MarkdownAppLinkProps = {
  readonly href?: string;
  readonly children?: ReactNode;
};

/**
 * Inline badge rendered by Streamdown for the `<applink>` tags that
 * `transformAppLinks` produces from app-internal markdown links. Navigation
 * goes through the router — no full reload, and no external-link confirmation.
 */
export function MarkdownAppLink({ href, children }: MarkdownAppLinkProps) {
  // The tag comes from LLM-authored markdown, so the badge — which tells the
  // user this is one of our pages and needs no confirmation — is only earned by
  // an href that resolves inside the app.
  if (!href || !isSameOriginPath(href)) {
    return <>{children}</>;
  }

  const Icon = href.startsWith("/listing/") ? ListIcon : ArrowRightIcon;

  return (
    <Link
      to={href}
      className={cn(
        "inline-flex max-w-[280px] items-center gap-1 rounded-full px-2 py-0.5",
        "align-baseline text-sm font-medium no-underline transition-colors",
        "bg-majorelle/10 text-majorelle hover:bg-majorelle/20",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-majorelle/40",
      )}
    >
      <Icon className="inline-block size-3 shrink-0 align-[-0.125em]" />
      <span className="min-w-0 truncate">{children}</span>
    </Link>
  );
}
